import { parkingOptions, STRUCTURED_PARKING } from 'views/ProFormaTable/constants';
import { AreaMode } from 'views/ProFormaWizard/constants';
import { UNIT_OF_MEASURE_TYPES, UNIT_TYPE_NAMES } from 'constants/unitTypes';
import { calculateBuildableArea } from 'views/ProFormaWizard/BasicInformation/hooks/useGrossBuildableArea';
import { calculateTotalDeveloperFee } from 'views/ProFormaWizard/helpers/calculateTotalDeveloperFeeHelper';
import { ProFormaQuickStartValues, DevelopmentValues } from './types';

export const BASIC_INFORMATION = 'Basic information';
export const DEVELOPMENT = 'Development';
export const OPERATIONS = 'Operations';

export const PRO_FORMA_QUICK_START_TAB = [BASIC_INFORMATION, DEVELOPMENT, OPERATIONS];

export const QUICK_START_LABEL_CLASS_NAME = 'pl-0 col-6 my-auto font-weight-normal text-dark text-nowrap';

const DEFAULT_LAND_AREA = 43560;
const DEFAULT_FLOOR_AREA_RATIO = 6;
const DEFAULT_LOT = {
    id: 1,
    landArea: DEFAULT_LAND_AREA,
    landAreaUnitTypeId: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.GROSS_SF].id,
    floorAreaRatio: DEFAULT_FLOOR_AREA_RATIO,
    grossBuildableArea: DEFAULT_FLOOR_AREA_RATIO * DEFAULT_LAND_AREA,
    areaMode: AreaMode.FAR,
};

export const initialBasicInformationValues = {
    buildings: 1,
    builtGrossSquareFootage: 261360,
    stories: 12,
    sellingCosts: 0.0175,
    projectedCapRateAtSale: 0.052,
    currentMarketCapRate: 0.05,
    parking: parkingOptions.filter(option => option.value === STRUCTURED_PARKING),
    structuredParkingSpaces: 180,
    surfaceParkingSpaces: null,
    trailerParkingSpaces: null,
    undergroundParkingSpaces: null,
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    constructionStart: new Date(new Date().getFullYear() + 2, new Date().getMonth(), 1),
    constructionDuration: 24,
    holdPeriod: 60,
    saleDate: new Date(new Date().getFullYear() + 7, new Date().getMonth(), 1),
    grossBuildableUnit: 'sqft',
    calculateArea: false,
    buildableLots: { grossBuildableAreaLots: [{ ...DEFAULT_LOT }], ...calculateBuildableArea([DEFAULT_LOT]) },
};

export const initialDevelopmentValues: DevelopmentValues = {
    acquisitionCosts: 7500000,
    hardCosts: 57630000 / 261360,
    totalHardCosts: 57630000,
    softCosts: 8230000 / 261360,
    totalSoftCosts: 8230000,
    interestReserve: 4150000,
    developerFee: 2,
    developerFeeAmount: calculateTotalDeveloperFee({
        developerFeePercent: 2 / 100,
        acquisitionCosts: 7500000,
        totalHardCosts: 57630000,
        totalSoftCosts: 8230000,
        interestReserve: 4150000,
    }),
    hardCostType: 'sf',
    softCostType: 'sf',
    sources: [
        {
            id: 1,
            name: 'Senior Construction Debt',
            amount: 52420000,
            type: 'other',
        },
        {
            id: 2,
            name: 'GP Equity',
            amount: 4030000,
            type: 'equity_gp',
        },
        {
            id: 3,
            name: 'LP Equity',
            amount: 24200000,
            type: 'equity_lp',
        },
    ],
};

export const initialOperationsValues = {
    unitArray: [
        {
            id: 1,
            name: 'Studio',
            amountPerMonth: 1625,
            bedrooms: 0,
            bathrooms: 1,
            averageSquareFootage: 520,
            units: 30,
        },
    ],
    tenantArray: [
        {
            id: 1,
            tenant: 'Tenant 1',
            suite: '',
            squareFootage: 0,
            rentGrowth: 0,
            valuePerSfPerYear: 0,
            leaseStart: '',
            rentStart: '',
            recoveryPercentage: 0,
            recoveryStart: '',
        },
    ],
    incomeArray: [
        {
            id: 1,
            name: 'Parking',
            annualGrowth: 0.025,
            valuePerUnitPerMonth: 125,
            amountPerYear: 1500,
            percentageOfTotalRentalIncome: 0.1,
        },
        {
            id: 2,
            name: 'Amenity Fees & Laundry',
            annualGrowth: 0.025,
            valuePerUnitPerMonth: 25,
            amountPerYear: 300,
            percentageOfTotalRentalIncome: 0.1,
        },
    ],
    operatingInfoValues: {
        rentalGrowthPercentage: 0.025,
        occupancyUponStabilizationPercentage: 0.94,
        percentPreLeased: 0.2,
        leaseUpPace: 20,
        lengthOfLease: 13,
        initialFreeRent: 1,
        stabilizedFreeRent: 0.5,
        generalVacancy: 0.06,
        creditLoss: 0.005,
    },
    expensesValues: {
        expenses: 0,
        totalExpenses: 0,
        expensesType: 'units',
    },
    expensesDetail: [
        {
            id: 1,
            name: 'Property Management',
            annualGrowth: 2.5,
            amountPerYear: 309950,
        },
        {
            id: 2,
            name: 'Repair & Maintenance',
            annualGrowth: 3,
            amountPerYear: 135000,
        },
        {
            id: 3,
            name: 'Utilities (net to owner)',
            annualGrowth: 3,
            amountPerYear: 86400,
        },
        {
            id: 4,
            name: 'Insurance',
            annualGrowth: 5,
            amountPerYear: 72000,
        },
        {
            id: 5,
            name: 'Property Taxes',
            annualGrowth: 4,
            amountPerYear: 967800,
        },
        {
            id: 6,
            name: 'Admin & Payroll',
            annualGrowth: 3,
            amountPerYear: 90000,
        },
        {
            id: 7,
            name: 'Capital Reserves',
            annualGrowth: 0,
            amountPerYear: 45000,
        },
    ],
};

export const initialProFormaValues: ProFormaQuickStartValues = {
    basicInformation: initialBasicInformationValues,
    development: initialDevelopmentValues,
    operations: initialOperationsValues,
};
